import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { to: "/", label: "首页" },
  { to: "/category", label: "电视剧分类" },
  { to: "/search", label: "搜索" },
  { to: "/about", label: "关于" },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const onHero = location.pathname === "/" && !scrolled;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const goSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = keyword.trim();
    navigate(query ? `/search?q=${encodeURIComponent(query)}` : "/search");
    setKeyword("");
    setMenuOpen(false);
  };

  const searchField = (
    <form onSubmit={goSearch} className="relative w-full" data-readdy-form="header-search">
      <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-foreground-500"></i>
      <input
        value={keyword}
        onChange={(event) => setKeyword(event.target.value)}
        placeholder="搜索剧名、演员或类型"
        aria-label="搜索剧集"
        className="h-10 w-full rounded-full border border-background-300/70 bg-background-100/70 pl-10 pr-20 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/25"
      />
      <button
        type="submit"
        className="absolute right-1 top-1 h-8 cursor-pointer rounded-full bg-primary-500 px-3 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
      >
        搜索
      </button>
    </form>
  );

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 h-[72px] transition-colors duration-300 ${
        onHero
          ? "bg-gradient-to-b from-background-50/95 via-background-50/55 to-transparent"
          : "border-b border-background-200/70 bg-background-50/92 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex h-full w-full items-center gap-6 px-4 md:px-8 lg:px-10">
        <Link to="/" className="flex shrink-0 cursor-pointer items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 font-heading text-lg font-semibold text-background-50">
            结
          </span>
          <span className="flex flex-col leading-tight">
            <span className="text-sm font-semibold tracking-wide text-foreground-950 md:text-base">
              波多野结衣家庭教师
            </span>
            <span className="mt-0.5 font-label text-[10px] uppercase tracking-[0.34em] text-foreground-500">
              Drama Journal
            </span>
          </span>
        </Link>

        <nav className="hidden flex-1 items-center justify-center gap-1 lg:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `cursor-pointer rounded-full px-4 py-2 font-label text-sm transition-colors ${
                  isActive
                    ? "bg-primary-500/12 text-primary-500"
                    : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto hidden w-[300px] shrink-0 lg:block">{searchField}</div>

        <div className="ml-auto flex items-center gap-2 lg:hidden">
          <Link
            to="/search"
            aria-label="前往搜索页"
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-300/70 bg-background-100/70 text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-500"
          >
            <i className="ri-search-line text-base"></i>
          </Link>
          <button
            type="button"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-300/70 bg-background-100/70 text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-500"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-lg`}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200/70 bg-background-50/98 backdrop-blur-md lg:hidden">
          <div className="px-4 py-4">
            {searchField}
            <nav className="mt-3 grid grid-cols-2 gap-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `flex items-center justify-between rounded-md border px-3 py-2.5 font-label text-sm transition-colors ${
                      isActive
                        ? "border-primary-500/40 bg-primary-500/12 text-primary-500"
                        : "border-background-200/70 bg-background-100 text-foreground-700"
                    }`
                  }
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-base opacity-60"></i>
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}